import { defineMessages, plural } from "../i18n/core";

export const organizationsMessages = defineMessages({
  "pt-BR": {
    title: "Organizações",
    lede: (customers: number, machines: number, protectedGb: string) =>
      `${customers} ${plural(customers, "cliente", "clientes")} · ${machines} ${plural(machines, "máquina", "máquinas")} · ${protectedGb} protegidos no total.`,
    newOrg: "Nova organização",
    editOrg: "Editar",
    createTitle: "Nova organização",
    editTitle: (name: string) => `Editar ${name}`,
    fieldName: "Nome",
    fieldKind: "Tipo",
    kindCustomer: "Cliente",
    kindMsp: "MSP (revenda)",
    kindHint: "MSP enxerga as organizações filhas; cliente enxerga só a si.",
    fieldParent: "Pertence ao MSP",
    parentNone: "Nenhum (cliente direto)",
    fieldQuota: "Quota no armazenamento (GB)",
    quotaHint: "Quanto a organização pode ocupar na primeira área de armazenamento. Vazio: sem limite. Depois, altere em Administração › Armazenamento.",
    quotaAssigned: (quota: string) => `Quota atribuída: ${quota}. Para alterar, use Administração › Armazenamento.`,
    quotaAssignedNoLimit: "Sem limite de armazenamento. Para definir uma quota, use Administração › Armazenamento.",
    storedNoLimit: (used: string) => `${used} ocupados, sem limite`,
    noStorage: "Sem armazenamento atribuído",
    fieldLocale: "Idioma do grupo e da política padrão",
    save: "Salvar",
    create: "Criar organização",
    created: (name: string, pool: string) => `Organização "${name}" criada em ${pool}.`,
    createdNoPool: (name: string) => `Organização "${name}" criada, mas sem armazenamento: atribua uma área antes do primeiro backup.`,
    saved: (name: string) => `Organização "${name}" salva.`,
    createFailed: "Não foi possível criar a organização.",
    saveFailed: "Não foi possível salvar.",
    nameRequired: "Dê um nome à organização.",
    colCustomer: "Cliente",
    colStatus: "Situação",
    colMachines: "Máquinas",
    colQuota: "Ocupado / quota",
    open: "Abrir",
    statusOk: "Tudo protegido",
    statusAttention: "Atenção",
    statusCritical: "Backup falhando",
    quotaUsed: (used: string, quota: string) => `${used} de ${quota}`,
    // Encerramento (ADR 0024)
    closeOrg: "Encerrar",
    statusClosed: "Encerrada",
    // Teste gratuito (ADR 0038)
    trialUntil: (date: string) => `Teste até ${date}`,
    trialEnded: "Teste encerrado",
    trialSection: "Teste gratuito",
    trialStatus: (date: string) => `O teste vai até ${date}.`,
    trialStatusEnded: (date: string) => `O teste terminou em ${date}; os backups estão parados e restaurar continua liberado durante o prazo de restauração.`,
    trialKeep: "Manter o prazo",
    trialExtend: "Prorrogar até",
    trialExtendDate: "Nova data de fim do teste",
    trialConvert: "Converter em cliente",
    trialConvertHint: "Tira o prazo: a organização passa a cliente e os backups seguem sem data para parar.",
    trialDateRequired: "Escolha a nova data de fim do teste.",
    trialExtended: (name: string, date: string) => `Teste de "${name}" prorrogado até ${date}.`,
    trialConverted: (name: string) => `"${name}" agora é cliente.`,
    purgeOn: (when: string) => `Dados apagados ${when}`,
    purgeNow: "Apagando os dados…",
    purgeStuck: (detail: string) => `O expurgo não terminou: ${detail}`,
    reopen: "Reabrir",
    purge: "Apagar agora",
    closeTitle: (name: string) => `Encerrar ${name}?`,
    closeExplain: (name: string, days: number) =>
      `A organização some da console, os agentes param e tokens e convites vencem. Os backups ficam guardados por ${days} dias, prazo para reabrir. Depois disso tudo é apagado. Para confirmar, digite ${name}.`,
    closeConfirmField: "Nome da organização",
    closeMismatch: "O nome não confere.",
    closePurgeNow: "Apagar os dados agora, sem os 30 dias",
    closePurgeNowHint: "Para organização de teste ou cliente que já pediu a saída. Não tem volta.",
    closed: (name: string) => `${name} encerrada.`,
    closedAndPurging: (name: string) => `${name} encerrada; os dados estão sendo apagados.`,
    closeFailed: "Não foi possível encerrar.",
    reopened: (name: string) => `${name} reaberta. Os agentes voltam sozinhos.`,
    reopenFailed: "Não foi possível reabrir.",
    purgeTitle: (name: string) => `Apagar os dados de ${name} agora?`,
    purgeExplain: "Todos os backups de todas as máquinas são apagados dos pools e das réplicas, e o cadastro some. Não tem volta.",
    purgeRequested: (name: string) => `Expurgo de ${name} iniciado. A organização some da lista quando terminar.`,
    purgeFailed: "Não foi possível pedir o expurgo.",
  },
  "en-US": {
    title: "Organizations",
    lede: (customers: number, machines: number, protectedGb: string) =>
      `${customers} ${plural(customers, "customer", "customers")} · ${machines} ${plural(machines, "machine", "machines")} · ${protectedGb} protected in total.`,
    newOrg: "New organization",
    editOrg: "Edit",
    createTitle: "New organization",
    editTitle: (name: string) => `Edit ${name}`,
    fieldName: "Name",
    fieldKind: "Type",
    kindCustomer: "Customer",
    kindMsp: "MSP (reseller)",
    kindHint: "An MSP sees its child organizations; a customer sees only itself.",
    fieldParent: "Belongs to MSP",
    parentNone: "None (direct customer)",
    fieldQuota: "Storage quota (GB)",
    quotaHint: "How much the organization may occupy in its first storage area. Empty: no limit. Later, change it under Administration › Storage.",
    quotaAssigned: (quota: string) => `Assigned quota: ${quota}. To change it, use Administration › Storage.`,
    quotaAssignedNoLimit: "No storage limit. To set a quota, use Administration › Storage.",
    storedNoLimit: (used: string) => `${used} used, no limit`,
    noStorage: "No storage assigned",
    fieldLocale: "Language of the default group and policy",
    save: "Save",
    create: "Create organization",
    created: (name: string, pool: string) => `Organization "${name}" created in ${pool}.`,
    createdNoPool: (name: string) => `Organization "${name}" created, but with no storage: assign an area before the first backup.`,
    saved: (name: string) => `Organization "${name}" saved.`,
    createFailed: "Could not create the organization.",
    saveFailed: "Could not save.",
    nameRequired: "Give the organization a name.",
    colCustomer: "Customer",
    colStatus: "Status",
    colMachines: "Machines",
    colQuota: "Used / quota",
    open: "Open",
    statusOk: "All protected",
    statusAttention: "Attention",
    statusCritical: "Backups failing",
    quotaUsed: (used: string, quota: string) => `${used} of ${quota}`,
    // Closure (ADR 0024)
    closeOrg: "Close",
    statusClosed: "Closed",
    // Free trial (ADR 0038)
    trialUntil: (date: string) => `Trial until ${date}`,
    trialEnded: "Trial ended",
    trialSection: "Free trial",
    trialStatus: (date: string) => `The trial runs until ${date}.`,
    trialStatusEnded: (date: string) => `The trial ended on ${date}; backups are stopped and restores remain available during the restore period.`,
    trialKeep: "Keep the end date",
    trialExtend: "Extend until",
    trialExtendDate: "New trial end date",
    trialConvert: "Convert to customer",
    trialConvertHint: "Removes the end date: the organization becomes a customer and backups keep running with no date to stop.",
    trialDateRequired: "Choose the new trial end date.",
    trialExtended: (name: string, date: string) => `Trial of "${name}" extended until ${date}.`,
    trialConverted: (name: string) => `"${name}" is now a customer.`,
    purgeOn: (when: string) => `Data deleted ${when}`,
    purgeNow: "Deleting data…",
    purgeStuck: (detail: string) => `The purge did not finish: ${detail}`,
    reopen: "Reopen",
    purge: "Delete now",
    closeTitle: (name: string) => `Close ${name}?`,
    closeExplain: (name: string, days: number) =>
      `The organization disappears from the console, agents stop and tokens and invitations expire. Backups are kept for ${days} days, the window to reopen. After that everything is deleted. To confirm, type ${name}.`,
    closeConfirmField: "Organization name",
    closeMismatch: "The name does not match.",
    closePurgeNow: "Delete the data now, without the 30 days",
    closePurgeNowHint: "For a test organization or a customer that already asked to leave. Cannot be undone.",
    closed: (name: string) => `${name} closed.`,
    closedAndPurging: (name: string) => `${name} closed; its data is being deleted.`,
    closeFailed: "Could not close.",
    reopened: (name: string) => `${name} reopened. Agents come back on their own.`,
    reopenFailed: "Could not reopen.",
    purgeTitle: (name: string) => `Delete the data of ${name} now?`,
    purgeExplain: "Every backup of every machine is deleted from the pools and replicas, and the registration is removed. Cannot be undone.",
    purgeRequested: (name: string) => `Purge of ${name} started. The organization leaves the list when it finishes.`,
    purgeFailed: "Could not request the purge.",
  },
  es: {
    title: "Organizaciones",
    lede: (customers: number, machines: number, protectedGb: string) =>
      `${customers} ${plural(customers, "cliente", "clientes")} · ${machines} ${plural(machines, "equipo", "equipos")} · ${protectedGb} protegidos en total.`,
    newOrg: "Nueva organización",
    editOrg: "Editar",
    createTitle: "Nueva organización",
    editTitle: (name: string) => `Editar ${name}`,
    fieldName: "Nombre",
    fieldKind: "Tipo",
    kindCustomer: "Cliente",
    kindMsp: "MSP (distribuidor)",
    kindHint: "Un MSP ve sus organizaciones hijas; un cliente se ve solo a sí mismo.",
    fieldParent: "Pertenece al MSP",
    parentNone: "Ninguno (cliente directo)",
    fieldQuota: "Cuota en el almacenamiento (GB)",
    quotaHint: "Cuánto puede ocupar la organización en su primera área de almacenamiento. Vacío: sin límite. Después, cámbiala en Administración › Almacenamiento.",
    quotaAssigned: (quota: string) => `Cuota asignada: ${quota}. Para cambiarla, usa Administración › Almacenamiento.`,
    quotaAssignedNoLimit: "Sin límite de almacenamiento. Para definir una cuota, usa Administración › Almacenamiento.",
    storedNoLimit: (used: string) => `${used} ocupados, sin límite`,
    noStorage: "Sin almacenamiento asignado",
    fieldLocale: "Idioma del grupo y la política predeterminados",
    save: "Guardar",
    create: "Crear organización",
    created: (name: string, pool: string) => `Organización "${name}" creada en ${pool}.`,
    createdNoPool: (name: string) => `Organización "${name}" creada, pero sin almacenamiento: asigna un área antes de la primera copia.`,
    saved: (name: string) => `Organización "${name}" guardada.`,
    createFailed: "No se pudo crear la organización.",
    saveFailed: "No se pudo guardar.",
    nameRequired: "Ponle un nombre a la organización.",
    colCustomer: "Cliente",
    colStatus: "Estado",
    colMachines: "Equipos",
    colQuota: "Ocupado / cuota",
    open: "Abrir",
    statusOk: "Todo protegido",
    statusAttention: "Atención",
    statusCritical: "Copias fallando",
    quotaUsed: (used: string, quota: string) => `${used} de ${quota}`,
    // Cierre (ADR 0024)
    closeOrg: "Cerrar",
    statusClosed: "Cerrada",
    // Prueba gratis (ADR 0038)
    trialUntil: (date: string) => `Prueba hasta el ${date}`,
    trialEnded: "Prueba terminada",
    trialSection: "Prueba gratuita",
    trialStatus: (date: string) => `La prueba dura hasta el ${date}.`,
    trialStatusEnded: (date: string) => `La prueba terminó el ${date}; las copias están detenidas y restaurar sigue disponible durante el plazo de restauración.`,
    trialKeep: "Mantener la fecha",
    trialExtend: "Prorrogar hasta",
    trialExtendDate: "Nueva fecha de fin de la prueba",
    trialConvert: "Convertir en cliente",
    trialConvertHint: "Quita la fecha: la organización pasa a cliente y las copias siguen sin fecha para detenerse.",
    trialDateRequired: "Elige la nueva fecha de fin de la prueba.",
    trialExtended: (name: string, date: string) => `Prueba de "${name}" prorrogada hasta el ${date}.`,
    trialConverted: (name: string) => `"${name}" ahora es cliente.`,
    purgeOn: (when: string) => `Datos borrados ${when}`,
    purgeNow: "Borrando los datos…",
    purgeStuck: (detail: string) => `El borrado no terminó: ${detail}`,
    reopen: "Reabrir",
    purge: "Borrar ahora",
    closeTitle: (name: string) => `¿Cerrar ${name}?`,
    closeExplain: (name: string, days: number) =>
      `La organización desaparece de la consola, los agentes se detienen y los tokens e invitaciones vencen. Las copias se guardan ${days} días, plazo para reabrir. Después se borra todo. Para confirmar, escriba ${name}.`,
    closeConfirmField: "Nombre de la organización",
    closeMismatch: "El nombre no coincide.",
    closePurgeNow: "Borrar los datos ahora, sin los 30 días",
    closePurgeNowHint: "Para una organización de prueba o un cliente que ya pidió la baja. No se puede deshacer.",
    closed: (name: string) => `${name} cerrada.`,
    closedAndPurging: (name: string) => `${name} cerrada; sus datos se están borrando.`,
    closeFailed: "No se pudo cerrar.",
    reopened: (name: string) => `${name} reabierta. Los agentes vuelven solos.`,
    reopenFailed: "No se pudo reabrir.",
    purgeTitle: (name: string) => `¿Borrar los datos de ${name} ahora?`,
    purgeExplain: "Todas las copias de todas las máquinas se borran de los pools y las réplicas, y el registro desaparece. No se puede deshacer.",
    purgeRequested: (name: string) => `Borrado de ${name} iniciado. La organización sale de la lista cuando termina.`,
    purgeFailed: "No se pudo pedir el borrado.",
  },
});
